import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Activity as ActivityIcon, BookOpen, FileSearch, ImageUp, Inbox, Search, X } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActivityRow, ReviewActions } from '@/components/admin';
import { CheckRow } from '@/components/checks';
import { REASON_LABEL } from '@/components/coins';
import { PageHeader } from '@/components/layout/AppShell';
import { PurchaseRow, TransactionRow } from '@/components/lists';
import { Page, Rise } from '@/components/motion';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { EmptyState, ErrorState, Pager, Tabs } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import type { CheckStatus, LedgerReason, PurchaseStatus, ScreenshotStatus } from '@/lib/api/types';
import { isInProgress } from '@/lib/visa';
import { CHECK_FILTERS, ListSkeleton } from '../ChecksPage';
import { PURCHASE_FILTERS } from '../PurchasesPage';

/** `?user_id=` narrows any admin list to one user. */
function useUserFilter(): [string | undefined, () => void] {
  const [params, setParams] = useSearchParams();
  const userId = params.get('user_id') ?? undefined;
  return [
    userId,
    () => {
      params.delete('user_id');
      setParams(params, { replace: true });
    },
  ];
}

function UserChip({ userId, onClear }: { userId?: string; onClear: () => void }) {
  if (!userId) return null;
  return (
    <Badge tone="info" className="gap-2 py-1">
      One user only
      <button type="button" onClick={onClear} className="rounded-full hover:bg-sky-500/20" title="Show everyone">
        <X className="size-3" />
      </button>
    </Badge>
  );
}

function ListBody<T>({
  q,
  empty,
  render,
}: {
  q: UseQueryResult<T[]>;
  empty: React.ReactNode;
  render: (item: T) => React.ReactNode;
}) {
  if (q.isPending) return <ListSkeleton />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.data.length === 0) return <>{empty}</>;
  return <div className="divide-y">{q.data.map(render)}</div>;
}

export function AdminPurchasesPage() {
  const [userId, clearUser] = useUserFilter();
  const [status, setStatus] = React.useState<PurchaseStatus | 'all'>('pending');
  const [offset, setOffset] = React.useState(0);
  const LIMIT = 20;
  const params = { status: status === 'all' ? undefined : status, user_id: userId, limit: LIMIT, offset };
  const q = useQuery({
    queryKey: ['admin', 'purchases', params],
    queryFn: () => adminApi.purchases(params),
    placeholderData: keepPreviousData,
    refetchInterval: status === 'pending' ? 20_000 : false,
  });
  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Review purchases" description="Check each receipt against the bank, then approve to credit the coins, or decline with a reason." />
      <Rise className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs layoutId="admin-purchase-filter" value={status} onChange={(v) => { setStatus(v); setOffset(0); }} options={PURCHASE_FILTERS} />
        <UserChip userId={userId} onClear={clearUser} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          <ListBody
            q={q}
            empty={<EmptyState icon={<Inbox />} title={status === 'pending' ? 'Queue is empty 🎉' : 'No purchases'} description={status === 'pending' ? 'Nothing waiting for review.' : undefined} />}
            render={(p) => <PurchaseRow key={p.id} purchase={p} showUser actions={<ReviewActions purchase={p} />} />}
          />
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}

export function AdminChecksPage() {
  const [userId, clearUser] = useUserFilter();
  const [status, setStatus] = React.useState<CheckStatus | 'all'>('all');
  const [offset, setOffset] = React.useState(0);
  const LIMIT = 25;
  const params = { status: status === 'all' ? undefined : status, user_id: userId, limit: LIMIT, offset };
  const q = useQuery({
    queryKey: ['admin', 'checks', params],
    queryFn: () => adminApi.checks(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.some((c) => isInProgress(c.status)) ? 5000 : false),
  });
  return (
    <Page>
      <PageHeader eyebrow="Admin" title="All checks" description="Every customer's visa checks." />
      <Rise className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs layoutId="admin-check-filter" value={status} onChange={(v) => { setStatus(v); setOffset(0); }} options={CHECK_FILTERS} />
        <UserChip userId={userId} onClear={clearUser} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          <ListBody q={q} empty={<EmptyState icon={<FileSearch />} title="No checks" />} render={(c) => <CheckRow key={c.id} check={c} to={`/admin/checks/${c.id}`} showUser />} />
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}

const SCREENSHOT_FILTERS: { value: ScreenshotStatus; label: string }[] = [
  { value: 'pending', label: 'Waiting' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

/** Checks the automation gave up on, with a portal screenshot from the customer. */
export function AdminScreenshotsPage() {
  const [status, setStatus] = React.useState<ScreenshotStatus>('pending');
  const [offset, setOffset] = React.useState(0);
  const LIMIT = 25;
  const params = { screenshot_status: status, limit: LIMIT, offset };
  const q = useQuery({
    queryKey: ['admin', 'checks', params],
    queryFn: () => adminApi.checks(params),
    placeholderData: keepPreviousData,
    refetchInterval: status === 'pending' ? 20_000 : false,
  });
  return (
    <Page>
      <PageHeader
        eyebrow="Admin"
        title="Review screenshots"
        description="When the automation can't reach the portal, customers send a screenshot of their result. Open each one, check the values against the image, then approve or decline."
      />
      <Rise className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs layoutId="admin-screenshot-filter" value={status} onChange={(v) => { setStatus(v); setOffset(0); }} options={SCREENSHOT_FILTERS} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          <ListBody
            q={q}
            empty={<EmptyState icon={<ImageUp />} title={status === 'pending' ? 'Queue is empty 🎉' : 'No screenshots'} description={status === 'pending' ? 'Nothing waiting for review.' : undefined} />}
            render={(c) => <CheckRow key={c.id} check={c} to={`/admin/checks/${c.id}`} showUser />}
          />
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}

const REASONS = Object.keys(REASON_LABEL) as LedgerReason[];

export function AdminLedgerPage() {
  const [userId, clearUser] = useUserFilter();
  const [reason, setReason] = React.useState<LedgerReason | ''>('');
  const [offset, setOffset] = React.useState(0);
  const LIMIT = 50;
  const params = { reason: reason || undefined, user_id: userId, limit: LIMIT, offset };
  const q = useQuery({ queryKey: ['admin', 'transactions', params], queryFn: () => adminApi.transactions(params), placeholderData: keepPreviousData });
  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Coin ledger" description="Every change to every balance." />
      <Rise className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={reason} onChange={(e) => { setReason(e.target.value as LedgerReason | ''); setOffset(0); }} className="w-56">
          <option value="">All reasons</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>{REASON_LABEL[r]}</option>
          ))}
        </Select>
        <UserChip userId={userId} onClear={clearUser} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          <ListBody q={q} empty={<EmptyState icon={<BookOpen />} title="No entries" />} render={(t) => <TransactionRow key={t.id} tx={t} showUser />} />
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}

const ACTION_PRESETS = [
  { value: '', label: 'All' },
  { value: 'auth.', label: 'Sign-ins' },
  { value: 'purchase.', label: 'Purchases' },
  { value: 'check.', label: 'Checks' },
  { value: 'coins.adjust', label: 'Coin adjustments' },
  { value: 'user.update', label: 'User changes' },
  { value: 'settings.update', label: 'Pricing' },
  { value: 'bank_account.', label: 'Bank accounts' },
];

export function AdminActivityPage() {
  const [userId, clearUser] = useUserFilter();
  const [action, setAction] = React.useState('');
  const [custom, setCustom] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const LIMIT = 50;
  const effective = custom.trim() || action;
  const params = { action: effective || undefined, user_id: userId, limit: LIMIT, offset };
  const q = useQuery({ queryKey: ['admin', 'activity', params], queryFn: () => adminApi.activity(params), placeholderData: keepPreviousData });
  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Activity log" description="Who did what, and when." />
      <Rise className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={action} onChange={(e) => { setAction(e.target.value); setCustom(''); setOffset(0); }} className="w-48">
          {ACTION_PRESETS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </Select>
        <div className="w-60">
          <Input icon={<Search />} placeholder="Exact action or prefix." value={custom} onChange={(e) => { setCustom(e.target.value); setOffset(0); }} />
        </div>
        <UserChip userId={userId} onClear={clearUser} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          <ListBody q={q} empty={<EmptyState icon={<ActivityIcon />} title="No activity" />} render={(a) => <ActivityRow key={a.id} a={a} />} />
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}
